/**
 * Standardized z-index values for consistent layering across the application.
 * 
 * Z-index ranges:
 * - 0-99: Base content and subtle layering
 * - 100-999: Interactive elements (dropdowns, menus)
 * - 1000-1999: Modal backdrops and overlays
 * - 2000-2999: Modal content
 * - 3000-3999: Popovers and floating elements
 * - 4000-4999: Tooltips
 * - 5000-5999: Notifications and toasts
 * - 6000-9999: Critical system overlays
 * - 10000+: Reserved for edge cases only
 */

export const Z_INDEX = {
  // Base layers (0-99)
  base: 0,
  elevated: 1,
  sticky: 10,
  stickyHeader: 15,
  tableHeader: 20,
  
  // Interactive elements (100-999)
  dropdown: 100,
  dropdownBackdrop: 99,
  menu: 150,
  select: 200,
  autocomplete: 250,
  
  // Overlays and modals (1000-2999)
  modalBackdrop: 1000,
  modal: 2000,
  modalContent: 2100,
  
  // Floating elements (3000-3999)
  popover: 3000,
  popoverBackdrop: 2999,
  floatingPanel: 3100,
  
  // Tooltips (4000-4999)
  tooltip: 4000,
  
  // Notifications (5000-5999)
  notification: 5000,
  toast: 5100,
  alert: 5200,
  
  // Modal-specific popovers (99999-100000)
  modalPopoverBackdrop: 99999,
  modalPopover: 100000,
  
  // System-level (6000-9999)
  systemModal: 6000,
  criticalOverlay: 7000,
  debugPanel: 8000,
  
  // Maximum (use sparingly)
  maximum: 10000
} as const;

// Type for z-index values
export type ZIndexValue = typeof Z_INDEX[keyof typeof Z_INDEX];

// Helper function to get z-index with offset
export function getZIndex(layer: keyof typeof Z_INDEX, offset = 0): number {
  return Z_INDEX[layer] + offset;
}

// Special z-index generator for overlay service
export class DynamicZIndex {
  private static baseIndex = Z_INDEX.modal;
  private static currentIndex = DynamicZIndex.baseIndex;
  
  static getNext(): number {
    // Reset if we're getting too high
    if (DynamicZIndex.currentIndex > Z_INDEX.floatingPanel) {
      DynamicZIndex.currentIndex = DynamicZIndex.baseIndex;
    }
    return ++DynamicZIndex.currentIndex;
  }
  
  static reset(): void {
    DynamicZIndex.currentIndex = DynamicZIndex.baseIndex;
  }
}